import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  Divider,
  AlertTitle,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Tooltip,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { getVesselsByType } from '../firebase/services/vesselService';
import { TimeSlot, Vessel, MarinaSetting } from '../models/types';
import { createReservation, findAvailableVessel } from '../firebase/services/reservationService';
import AvailabilityCalendar from './AvailabilityCalendar';
import MultiDayTimeSlotSelector from './MultiDayTimeSlotSelector';
import { formatPhoneNumber } from '../utils/formatters';
import MultiPaymentForm from './MultiPaymentForm';
import { getMarinaSettings, getPolicies } from '../firebase/services/marinaSettingsService';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { validatePromoCode, applyPromoCode } from '../firebase/services/discountService';
import { CancelOutlined as CancelIcon } from '@mui/icons-material';
import { getDamageWaiverSettings, calculateDamageWaiverAmount } from '../firebase/services/damageWaiverService';
import { generateReservationId } from '../utils/generators';
import { trackFunnelStep } from '../firebase/services/abandonedCartService';
import { sessionRecorder } from '../utils/sessionRecorder';
import { getVesselPricingOptions } from '../firebase/services/pricingService';
import {
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  DirectionsBoat as BoatIcon,
  Kayaking as KayakIcon,
  SportsMotorsports as JetSkiIcon,
  SailingOutlined as SailingIcon,
  SurfingOutlined as PaddleboardIcon,
  PhishingOutlined as FishingIcon,
  SpeedOutlined as SpeedboatIcon,
  RowingOutlined as RowboatIcon
} from '@mui/icons-material';

// Define steps for the reservation process
const steps = ['Select Vessel', 'Select Date & Time', 'Customer Information', 'Damage Waiver', 'Rental Policies', 'Payment'];

// Add US states array for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' }
];

// Add common countries for passport dropdown
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CO', name: 'Colombia' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'RU', name: 'Russia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'SG', name: 'Singapore' },
  { code: 'IL', name: 'Israel' }
];

interface ReservationModalProps {
  open: boolean;
  onClose: () => void;
  disableNavigation?: boolean;
  isBookingSubdomain?: boolean;
  isStaffMode?: boolean;
}

const ReservationModal: React.FC<ReservationModalProps> = ({ open, onClose, disableNavigation, isBookingSubdomain = false, isStaffMode = false }) => {
  const { userProfile, userId } = useAuth();
  const { marinaSettings } = useSettings();
  
  // Add sessionId state
  const [sessionId, setSessionId] = useState<string>('');
  
  // State for the stepper
  const [activeStep, setActiveStep] = useState(0);
  
  // State for vessel selection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vesselTypes, setVesselTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [vessels, setVessels] = useState<{ [key: string]: Vessel[] }>({});
  const [partySize, setPartySize] = useState<string>('');
  const [availableVessels, setAvailableVessels] = useState<Vessel[]>([]);
  
  // State for reservation form
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [timeSlot, setTimeSlot] = useState<TimeSlot>('3');
  const [startTime, setStartTime] = useState('9:00 AM');
  const [endTime, setEndTime] = useState('12:00 PM');
  const [duration, setDuration] = useState('3');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [dob, setDob] = useState('');
  const [dlNumber, setDlNumber] = useState('');
  const [dlState, setDlState] = useState('CA'); // Default to California
  const [usePassport, setUsePassport] = useState(false);
  const [passportNumber, setPassportNumber] = useState('');
  const [passportCountry, setPassportCountry] = useState('');
  const [childrenCount, setChildrenCount] = useState('0');
  const [operatorsCount, setOperatorsCount] = useState('1');
  const [totalPrice, setTotalPrice] = useState(marinaSettings?.reservationRules?.baseRate || 0);
  const [showTimeSlots, setShowTimeSlots] = useState(true);
  const [confirmedVesselId, setConfirmedVesselId] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null); // No default selection
  const [durationOptions, setDurationOptions] = useState<Array<{ hours: number; price: number; description?: string }>>([]);
  const [loadingDurations, setLoadingDurations] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [reservationData, setReservationData] = useState<any>(null);
  const [settings, setSettings] = useState<MarinaSetting | null>(null);
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [policies, setPolicies] = useState<any>(null);
  const [damageWaiverSettings, setDamageWaiverSettings] = useState<any>(null);
  const [waiverAmount, setWaiverAmount] = useState<number>(25);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  
  // Add after other state declarations
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDetails, setPromoCodeDetails] = useState<any>(null);
  const [checkingPromoCode, setCheckingPromoCode] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  
  // Add state for damage waiver
  const [waiverAccepted, setWaiverAccepted] = useState<boolean | null>(null);
  
  // Add a new state variable for policy agreement
  const [policyAgreed, setPolicyAgreed] = useState<boolean>(false);
  
  // Add payment processing state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // Generate session ID when modal opens
  useEffect(() => {
    if (open) {
      // Generate a new session ID when the modal opens
      const newSessionId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      
      // Track session start
      trackFunnelStep({
        step: 'session_start',
        customerId: userId || 'guest',
        sessionId: newSessionId,
        data: {
          vesselType: selectedType,
          startDate: selectedDate,
          endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
          timeSlot: timeSlot,
          totalPrice: totalPrice,
          originalPrice: originalPrice,
          discountAmount: discountAmount,
          promoCode: promoCode
        },
        metadata: {
          deviceInfo: navigator.userAgent,
          browserInfo: navigator.appName,
          lastActivity: new Date()
        }
      }).catch(console.error);
    }
  }, [open]);
  
  // Fetch vessel types when modal opens
  useEffect(() => {
    const fetchVesselTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all active vessels
        const allVessels = await getVesselsByType();
        
        // Extract unique vessel types that have active vessels
        const types = Object.keys(allVessels).filter(type => allVessels[type].length > 0);
        
        setVesselTypes(types);
        setVessels(allVessels);
        
        // Set Pontoon as default vessel type if available, otherwise use first available type
        if (types.includes('pontoon')) {
          setSelectedType('pontoon');
        } else if (types.length > 0) {
          setSelectedType(types[0]);
        }
      } catch (err) {
        console.error('Error fetching vessel types:', err);
        setError('Failed to load vessel types. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchVesselTypes();
      fetchSettings();
      
      // Reset state when modal opens
      setActiveStep(0);
      setReservationSuccess(false);
      setReservationId(null);
    }
  }, [open]);
  
  // Load user data if available
  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName);
      setLastName(userProfile.lastName);
      setEmail(userProfile.email);
      setPhone(formatPhoneNumber(userProfile.phone));
    }
  }, [userProfile]);
  
  // Fetch settings
  const fetchSettings = async () => {
    try {
      const marinaSettings = await getMarinaSettings();
      setSettings(marinaSettings);
      
      // Fetch policies separately
      const policiesData = await getPolicies();
      setPolicies(policiesData);

      // Fetch damage waiver settings
      const waiverSettings = await getDamageWaiverSettings();
      setDamageWaiverSettings(waiverSettings);
    } catch (error) {
      console.error('Error fetching marina settings:', error);
    }
  };
  
  // Updates for time slot and rate calculations
  useEffect(() => {
    if (!availableVessels.length) return;
    
    // Use the first vessel's rates as a reference
    const baseRate = marinaSettings?.reservationRules?.baseRate || 0;
    
    // Set times based on selected time slot
    if (timeSlot === '3') {
      setStartTime('9:00 AM');
      setEndTime('12:00 PM');
      setDuration('3');
      setTotalPrice(baseRate);
    } else if (timeSlot === '7') {
      setStartTime('9:00 AM');
      setEndTime('4:00 PM');
      setDuration('7');
      setTotalPrice(baseRate * 2);
    }
    
    // Reset availability check when time slot changes
    setIsAvailable(null);
  }, [timeSlot, availableVessels, marinaSettings]);
  
  // Calculate waiver amount when needed
  useEffect(() => {
    const updateWaiverAmount = async () => {
      if (confirmedVesselId && selectedType && totalPrice > 0) {
        try {
          const amount = await calculateDamageWaiverAmount(
            confirmedVesselId,
            selectedType,
            totalPrice
          );
          setWaiverAmount(amount);
        } catch (err) {
          console.error('Error calculating damage waiver amount:', err);
          // Fallback to default
          setWaiverAmount(25);
        }
      }
    };

    if (activeStep === 3) {
      updateWaiverAmount();
    }
  }, [activeStep, confirmedVesselId, selectedType, totalPrice]);
  
  // Update the calculateTax function to use the correct property name for tax rate
  const calculateTax = (amount: number): number => {
    // Use the property name that actually exists in the settings object
    if (!settings?.reservationRules?.salesTaxRate) {
      // Default tax rate if settings not available
      return amount * 0.0825;
    }
    
    // Ensure we're calculating tax on a non-negative amount
    const taxableAmount = Math.max(0, amount);
    return taxableAmount * (settings.reservationRules.salesTaxRate / 100);
  };

  // Update tax amount whenever totalPrice changes
  useEffect(() => {
    const calculatedTax = calculateTax(totalPrice);
    setTaxAmount(calculatedTax);
  }, [totalPrice, settings?.reservationRules?.salesTaxRate]);
  
  // Load duration options when vessel type changes
  useEffect(() => {
    const loadDurationOptions = async () => {
      if (!selectedType) {
        setDurationOptions([]);
        return;
      }

      try {
        setLoadingDurations(true);
        const pricingOptions = await getVesselPricingOptions(selectedType);
        setDurationOptions(pricingOptions.durationOptions);
        
        // Don't auto-select any duration - user must choose
        // Reset selection if current selection is not available in new vessel type
        if (selectedDuration && pricingOptions.durationOptions.length > 0) {
          const availableDurations = pricingOptions.durationOptions.map(opt => opt.hours);
          if (!availableDurations.includes(selectedDuration)) {
            setSelectedDuration(null);
          }
        }
      } catch (error) {
        console.error('Error loading duration options:', error);
        // Fallback to default options
        setDurationOptions([
          { hours: 3, price: 300, description: 'Half-day adventure' },
          { hours: 7, price: 700, description: 'Full-day experience' }
        ]);
      } finally {
        setLoadingDurations(false);
      }
    };

    loadDurationOptions();
  }, [selectedType]);

  // Update total price when duration changes
  useEffect(() => {
    if (durationOptions.length > 0 && selectedDuration) {
      const selectedOption = durationOptions.find(opt => opt.hours === selectedDuration);
      if (selectedOption) {
        setTotalPrice(selectedOption.price);
        setOriginalPrice(selectedOption.price); // Set original price for discount calculations
      }
    }
  }, [selectedDuration, durationOptions]);

  // Handle duration change from time slot selector
  const handleDurationChange = (newDuration: number) => {
    setSelectedDuration(newDuration);
    // Keep the selected date but clear any selected time slot since duration changed
    setConfirmedVesselId(null);
    // Force time slots to refresh by briefly toggling showTimeSlots
    setShowTimeSlots(false);
    setTimeout(() => setShowTimeSlots(true), 10);
  };

  // Handlers for step 1: Vessel selection


  const handlePartySizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) { // Only allow digits
      setPartySize(value);
    }
  };
  
  const getMaxCapacity = () => {
    if (!selectedType || !vessels[selectedType]) return 0;
    return Math.max(...vessels[selectedType].map(v => v.capacity));
  };
  
  // Proceed to date selection step
  const handleProceedToDateSelection = () => {
    // Clear any existing errors
    setError(null);
    
    if (!selectedType || !partySize) {
      setError('Please select a vessel type and party size');
      return;
    }
    
    // Set available vessels for the selected type
    setAvailableVessels(vessels[selectedType] || []);
    setActiveStep(1);
  };
  
  // Handlers for step 2: Date and time selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setError(null); // Clear errors when selecting date
    setShowTimeSlots(true);
  };
  
  const handleTimeSlotSelect = (selectedTimeSlot: TimeSlot, price?: number, duration?: string | number) => {
    setError(null); // Clear errors when selecting time slot
    setTimeSlot(selectedTimeSlot);
    setShowTimeSlots(false);
    
    // Set start and end times based on the selected time slot
    if (selectedTimeSlot === '3') {
      setStartTime('9:00 AM');
      setEndTime('12:00 PM');
      setDuration('3');
    } else if (selectedTimeSlot === '7') {
      setStartTime('9:00 AM');
      setEndTime('4:00 PM');
      setDuration('7');
    } else if (selectedTimeSlot.includes('-')) {
      // Handle custom time slot format "HH:MM-HH:MM"
      const [start, end] = selectedTimeSlot.split('-');
      
      // Convert 24-hour format to 12-hour format for display
      const startHour = parseInt(start.split(':')[0]);
      const startMinute = parseInt(start.split(':')[1]);
      const endHour = parseInt(end.split(':')[0]);
      const endMinute = parseInt(end.split(':')[1]);
      
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      
      const formattedStart = `${startHour % 12 || 12}:${startMinute.toString().padStart(2, '0')} ${startPeriod}`;
      const formattedEnd = `${endHour % 12 || 12}:${endMinute.toString().padStart(2, '0')} ${endPeriod}`;
      
      setStartTime(formattedStart);
      setEndTime(formattedEnd);
      
      // Calculate duration in hours
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;
      const durationHours = (endTimeInMinutes - startTimeInMinutes) / 60;
      
      setDuration(durationHours.toString());
    }
    
    // Set the total price
    if (typeof price === 'number') {
      setTotalPrice(price);
    } else {
      // Default pricing logic
      const baseDuration = parseFloat(typeof duration === 'string' ? duration : duration?.toString() || '3');
      const basePrice = (marinaSettings?.reservationRules?.baseRate || 100);
      setTotalPrice(basePrice * (baseDuration >= 6 ? 2 : 1));
    }
    
    // Check and confirm available vessel
    checkAndConfirmAvailableVessel(selectedDate, selectedTimeSlot);
  };
  
  // Handler for multi-day time slot selector
  const handleMultiDayTimeSlotSelect = (date: Date, selectedTimeSlot: TimeSlot, price?: number, durationHours?: number) => {
    console.log('Multi-day time slot selected:', date, selectedTimeSlot, 'price:', price, 'duration:', durationHours);
    
    // Update the selected date if different
    if (!selectedDate || selectedDate.getTime() !== date.getTime()) {
      setSelectedDate(date);
    }
    
    setTimeSlot(selectedTimeSlot);
    setShowTimeSlots(false);
    setError(null);
    
    // Parse the time slot to set start and end times
    if (selectedTimeSlot.includes('-')) {
      const [start, end] = selectedTimeSlot.split('-');
      const startHour = parseInt(start.split(':')[0]);
      const endHour = parseInt(end.split(':')[0]);
      
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      
      const formattedStart = `${startHour % 12 || 12}:00 ${startPeriod}`;
      const formattedEnd = `${endHour % 12 || 12}:00 ${endPeriod}`;
      
      setStartTime(formattedStart);
      setEndTime(formattedEnd);
      
      // Use the duration from the selector if provided, otherwise calculate from time slot
      if (durationHours) {
        setDuration(durationHours.toString());
      } else {
        setDuration((endHour - startHour).toString());
      }
    }
    
    // Set the price if provided
    if (typeof price === 'number') {
      setTotalPrice(price);
    }
    
    checkAndConfirmAvailableVessel(date, selectedTimeSlot);
  };

  // Handler for date change from multi-day selector
  const handleDateChangeFromMultiDay = (date: Date) => {
    setSelectedDate(date);
    setShowTimeSlots(false);
    setTimeSlot('3');
    setStartTime('9:00 AM');
    setEndTime('12:00 PM');
    setDuration('3');
    setTotalPrice(marinaSettings?.reservationRules?.baseRate || 0);
    setConfirmedVesselId(null);
    sessionStorage.removeItem('confirmedAvailableVessel');
  };

  const checkAndConfirmAvailableVessel = async (date: Date, timeSlot: TimeSlot) => {
    if (!date) {
      setError('Invalid date selected. Please try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const dateString = format(date, 'yyyy-MM-dd');
      
      // Find an available vessel
      const availableVesselId = await findAvailableVessel(selectedType, parseInt(partySize), dateString, timeSlot);
      
      if (availableVesselId) {
        // Store confirmed vessel ID
        setConfirmedVesselId(availableVesselId);
        sessionStorage.setItem('confirmedAvailableVessel', JSON.stringify({
          vesselId: availableVesselId,
          date: dateString,
          timeSlot: timeSlot,
          timestamp: new Date().getTime()
        }));
        
        // Proceed to next step
        setActiveStep(2);
      } else {
        setError(`No vessels are available for ${format(date, 'MMMM d, yyyy')} at the selected time. Please select another time slot.`);
        setShowTimeSlots(true);
      }
    } catch (err) {
      console.error('Error checking vessel availability:', err);
      setError('Failed to check availability. Please try again.');
      setShowTimeSlots(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle phone number formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneNumber(e.target.value));
    setError(null); // Clear errors when changing phone
  };
  
  // Handle driver's license state selection
  const handleDlStateChange = (e: SelectChangeEvent<string>) => {
    setDlState(e.target.value);
  };
  
  // Handle passport country selection
  const handlePassportCountryChange = (e: SelectChangeEvent<string>) => {
    setPassportCountry(e.target.value);
  };
  
  // Handle promo code validation
  const handlePromoCodeValidation = async () => {
    if (!promoCode || !promoCode.trim()) {
      setPromoCodeError('Please enter a promo code');
      return;
    }

    // Clear general errors when attempting promo code validation
    setError(null);
    
    if (!selectedDate) {
      setPromoCodeError('Please select a date first');
      return;
    }
    
    setCheckingPromoCode(true);
    setPromoCodeError(null);
    setPromoCodeApplied(false);
    
    try {
      console.log('Validating promo code:', promoCode);
      console.log('Current User ID:', userId || 'guest');
      console.log('Total Price:', totalPrice);
      console.log('Vessel Type:', selectedType);
      console.log('Booking Date:', selectedDate);
      
      // Call the discount service to validate the promo code
      const result = await validatePromoCode(
        promoCode,
        userId || 'guest',
        totalPrice,
        selectedType,
        selectedDate
      );
      
      console.log('Promo code validation result:', result);
      
      if (!result) {
        setPromoCodeError('Invalid or expired promo code');
        setDiscountAmount(0);
        return;
      }
      
      // Store the discount information
      setPromoCodeDetails(result.discountDetails);
      setPromoCodeApplied(true);
      
      // Save original price if not already set
      if (originalPrice === 0) {
        setOriginalPrice(totalPrice);
      }
      
      // Apply the discount
      const discountAmt = result.discount;
      setDiscountAmount(discountAmt);
      
      // Calculate the new total price after discount
      const newTotalPrice = Math.max(0, totalPrice - discountAmt);
      console.log(`Applying discount of $${discountAmt} to total price $${totalPrice}, new price: $${newTotalPrice}`);
      
      // Set the new total price
      setTotalPrice(newTotalPrice);
      
      // Calculate the final price including damage waiver and tax
      const waiverCost = waiverAccepted ? waiverAmount : 0;
      const subtotal = newTotalPrice + waiverCost;
      const finalTaxAmount = calculateTax(subtotal);
      const finalPrice = subtotal + finalTaxAmount;
      
      // Calculate deposit (25% of total)
      const depositAmount = settings?.reservationRules?.depositType === 'percentage' ?
        roundToNearestQuarter(finalPrice * (settings.reservationRules.depositAmount / 100)) :
        settings?.reservationRules?.depositAmount || 0;
      
      // Store in localStorage for the payment form to access
      localStorage.setItem('reservationData', JSON.stringify({
        ...reservationData,
        deposit: depositAmount,
        isDepositPayment: true,
        depositPercentage: settings?.reservationRules?.depositType === 'percentage' ? settings.reservationRules.depositAmount : null,
        depositType: settings?.reservationRules?.depositType,
        rentalPrice: totalPrice,
        damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
        taxAmount: finalTaxAmount,
        finalPrice: finalPrice,
        originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
        discountAmount: discountAmount > 0 ? discountAmount : 0,
        promoCode: promoCodeApplied ? promoCode : null
      }));
      
      // Trigger a storage event so MultiPaymentForm immediately refreshes
      window.dispatchEvent(new Event('storage'));
      
      // Clear the error
      setPromoCodeError(null);
    } catch (err) {
      console.error('Error validating promo code:', err);
      setPromoCodeError('Failed to validate promo code');
      setDiscountAmount(0);
    } finally {
      setCheckingPromoCode(false);
    }
  };
  
  // Clear promo code application - also update this function
  const handleClearPromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoCodeDetails(null);
    setPromoCodeError(null);
    
    // Restore original price if discount was applied
    if (discountAmount > 0) {
      setTotalPrice(originalPrice);
      setDiscountAmount(0);
      
      // Calculate the final price including damage waiver and tax after removing promo code
      const waiverCost = waiverAccepted ? waiverAmount : 0;
      const subtotal = originalPrice + waiverCost;
      const finalTaxAmount = calculateTax(subtotal);
      const finalPrice = subtotal + finalTaxAmount;
      
      // Update localStorage with the restored price
      try {
        // Get current reservation data
        const reservationDataStr = localStorage.getItem('reservationData');
        let existingReservationData = {};
        if (reservationDataStr) {
          existingReservationData = JSON.parse(reservationDataStr);
        }
        
        // Create updated data without promo code
        const updatedReservationData = {
          ...existingReservationData,
          rentalPrice: originalPrice,      // Restore original price
          taxAmount: finalTaxAmount,
          finalPrice: finalPrice,          // Full final price with tax and waiver
          originalPrice: originalPrice,
          discountAmount: 0,
          promoCode: null,
          promoCodeDetails: null
        };
        
        // Store in localStorage
        localStorage.setItem('reservationData', JSON.stringify(updatedReservationData));
        console.log('Updated localStorage after removing promo code:', updatedReservationData);
        
        // Trigger a storage event so MultiPaymentForm immediately refreshes
        window.dispatchEvent(new Event('storage'));
      } catch (error) {
        console.error('Error updating localStorage after removing promo code:', error);
      }
      
      setOriginalPrice(0);
    }
  };
  
  // Helper function to round to nearest quarter (0.25)
  const roundToNearestQuarter = (amount: number): number => {
    return Math.round(amount * 4) / 4;
  };

  // Handler for submitting the reservation
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Track the payment step
      await trackFunnelStep({
        step: 'payment',
        customerId: userId || 'guest',
        sessionId: sessionId,
        data: {
          vesselType: selectedType,
          startDate: selectedDate,
          endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
          timeSlot: timeSlot,
          totalPrice: totalPrice,
          originalPrice: originalPrice,
          discountAmount: discountAmount,
          promoCode: promoCode,
          lastStep: activeStep,
          errorMessage: error
        },
        metadata: {
          deviceInfo: navigator.userAgent,
          browserInfo: navigator.appName,
          lastActivity: new Date()
        }
      });

      // Validate form
      if (!firstName || !lastName || !email || !phone) {
        setError('Please fill in all required fields');
        return;
      }
      
      if (!selectedDate || !timeSlot || !confirmedVesselId) {
        setError('Missing reservation details. Please start over.');
        return;
      }
      
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      
      // Calculate the final price including damage waiver if accepted
      // totalPrice already includes any applied discount
      const waiverCost = waiverAccepted ? waiverAmount : 0;
      const subtotal = totalPrice + waiverCost;
      const finalTaxAmount = calculateTax(subtotal);
      const finalPrice = subtotal + finalTaxAmount;
      
      // Create the reservation data object
      const reservationData = {
        customerId: userId ? userId : 'guest-' + Date.now(),
        customerDetails: {
          firstName,
          lastName,
          email,
          phone,
          dateOfBirth: dob,
          identification: usePassport 
            ? { type: 'passport', number: passportNumber, country: passportCountry }
            : { type: 'driversLicense', number: dlNumber, state: dlState },
          childrenCount: parseInt(childrenCount),
          operatorsCount: parseInt(operatorsCount)
        },
        vesselId: confirmedVesselId,
        vesselName: '', // Will be filled in by the server
        vesselType: selectedType,
        partySize: parseInt(partySize),
        date: dateString,
        timeSlot,
        startTime,
        endTime,
        rentalPrice: totalPrice, // Base rental price before waiver
        damageWaiverAccepted: waiverAccepted,
        damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
        taxAmount: finalTaxAmount, // Tax on total including waiver if selected
        finalPrice: finalPrice, // Final price including tax
        originalPrice: discountAmount > 0 ? originalPrice : totalPrice, // Always provide a value, never undefined
        discountAmount: discountAmount > 0 ? discountAmount : 0, // Always provide a value, never undefined
        discountId: promoCodeApplied && promoCodeDetails?.id ? promoCodeDetails.id : null, // Use null instead of undefined
        discountType: promoCodeApplied ? 'promo' : null, // Use null instead of undefined
        promoCode: promoCodeApplied ? promoCode : null, // Use null instead of undefined
        notes,
        status: 'confirmed',
        policyAgreed: policyAgreed,
        policyAgreedTimestamp: new Date().toISOString()
      };

      // Log the key pricing details before storing
      if (process.env.NODE_ENV === 'development') {
        console.log('Storing reservation data with pricing:');
        console.log('Total Price (after discounts):', totalPrice);
        console.log('Original Price (before discount):', originalPrice);
        console.log('Discount Amount:', discountAmount);
        console.log('Final Price (with tax and waiver):', finalPrice);
        console.log('Is 100% discount?', finalPrice === 0 || Math.abs(finalPrice) < 0.01);
      }

      // Calculate and store deposit info if applicable
      if (settings?.reservationRules?.depositRequired) {
        const totalWithWaiverAndTax = totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0));
        const calculatedDeposit = settings.reservationRules.depositType === 'percentage' ? 
          (totalWithWaiverAndTax * settings.reservationRules.depositAmount / 100) : 
          settings.reservationRules.depositAmount;
        
        // Round the deposit amount to nearest quarter
        const roundedDeposit = roundToNearestQuarter(calculatedDeposit);
        
        // Store in localStorage for the payment form to access
        localStorage.setItem('reservationData', JSON.stringify({
          ...reservationData,
          deposit: roundedDeposit,
          isDepositPayment: true,  // Explicitly mark this as a deposit payment
          depositPercentage: settings.reservationRules.depositType === 'percentage' ? settings.reservationRules.depositAmount : null,
          depositType: settings.reservationRules.depositType,
          rentalPrice: totalPrice,  // totalPrice already includes any discount
          damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
          taxAmount: finalTaxAmount,
          finalPrice: totalWithWaiverAndTax,  // this will also include any discount since totalPrice does
          originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
          discountAmount: discountAmount > 0 ? discountAmount : 0,
          promoCode: promoCodeApplied ? promoCode : null
        }));
      } else {
        // If this is a 100% discount (price near 0), force the finalPrice to be exactly 0
        // This ensures the payment form will show the zero-dollar interface
        const finalPriceForStorage = (finalPrice === 0 || Math.abs(finalPrice) < 0.01) ? 0 : finalPrice;
        
        // Store without deposit info
        localStorage.setItem('reservationData', JSON.stringify({
          ...reservationData,
          rentalPrice: totalPrice,  // totalPrice already includes any discount
          damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
          taxAmount: finalTaxAmount,
          finalPrice: finalPriceForStorage,  // Ensure exact 0 for zero-dollar reservations
          originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
          discountAmount: discountAmount > 0 ? discountAmount : 0,
          promoCode: promoCodeApplied ? promoCode : null
        }));
        
        console.log('Updated localStorage with finalPrice:', finalPriceForStorage);
      }
      
      // Store for payment processing
      setReservationData(reservationData);
      
      // Move to payment step
      setActiveStep(5);

      // Track successful completion
      await trackFunnelStep({
        step: 'confirmation',
        customerId: userId || 'guest',
        sessionId: sessionId,
        status: 'completed',
        data: {
          vesselType: selectedType,
          startDate: selectedDate,
          endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
          timeSlot: timeSlot,
          totalPrice: totalPrice,
          originalPrice: originalPrice,
          discountAmount: discountAmount,
          promoCode: promoCode,
          reservationId: reservationId
        },
        metadata: {
          deviceInfo: navigator.userAgent,
          browserInfo: navigator.appName,
          lastActivity: new Date()
        }
      });

      setReservationSuccess(true);

      // Track successful booking
      sessionRecorder.trackFunnelEvent({
        type: 'booking_completed',
        data: {
          step: 'confirmation'
        }
      });
    } catch (err: any) {
      console.error('Error preparing reservation:', err);
      setError(err.message || 'Failed to prepare reservation. Please try again.');
      
      // Track error
      sessionRecorder.trackFunnelEvent({
        type: 'payment_error',
        data: {
          error: {
            message: err.message || 'Unknown error',
            details: err
          }
        }
      });

      trackFunnelStep({
        step: 'error',
        customerId: userId || 'guest',
        sessionId: sessionId,
        status: 'abandoned',
        data: {
          vesselType: selectedType,
          startDate: selectedDate,
          endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
          timeSlot: timeSlot,
          totalPrice: totalPrice,
          originalPrice: originalPrice,
          discountAmount: discountAmount,
          promoCode: promoCode,
          error: err instanceof Error ? err.message : 'Unknown error'
        },
        metadata: {
          deviceInfo: navigator.userAgent,
          browserInfo: navigator.appName,
          lastActivity: new Date()
        }
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle reservation completion
  const handleReservationComplete = async (paymentId: string, status: string, processor: string) => {
    try {
      console.log('Payment completed with ID:', paymentId);
      
      // Immediately set processing state to prevent multiple payments
      setPaymentProcessing(true);
      
      // Calculate deposit amount if needed
      let depositAmount = 0;
      if (settings?.reservationRules?.depositRequired) {
        const totalWithWaiverAndTax = totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0));
        const calculatedDeposit = settings.reservationRules.depositType === 'percentage' ? 
          (totalWithWaiverAndTax * settings.reservationRules.depositAmount / 100) : 
          settings.reservationRules.depositAmount;
        
        // Round the deposit amount to nearest quarter
        depositAmount = roundToNearestQuarter(calculatedDeposit);
      }
      
      // Create payment info object for the reservation
      const paymentInfo = {
        id: paymentId,                                       // Square transaction ID
        amount: depositAmount > 0 ? depositAmount : totalPrice, // Actual amount paid (using rounded deposit)
        totalAmount: totalPrice,                             // Total reservation amount
        depositAmount: depositAmount,                        // Explicitly set the deposit amount
        remainingAmount: depositAmount > 0 ? 
          roundToNearestQuarter(totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0)) - depositAmount) : 0, // Rounded remaining amount
        depositPaid: depositAmount > 0,                      // Whether this is a deposit payment
        processor: processor as 'square',                    // Payment processor used
        status: (status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending') as 'paid' | 'pending' | 'failed', // Payment status
        paymentDate: new Date().toISOString(),               // Date/time of payment
        refunded: false,                                     // Track if payment has been refunded
        refundAmount: 0,                                     // Amount refunded (if applicable)
        refundDate: null,                                    // Date of refund (if applicable)
        refundId: null,                                      // Refund transaction ID (if applicable)
        paymentDetails: {                                    // Additional payment details
          cardBrand: null,                                   // Will be updated if available from processor
          last4: null,                                       // Last 4 digits of card (if available)
          paymentMethod: 'card'                              // Default to card
        }
      };
      
      // Get reservationData from state - this contains all the reservation details
      if (!reservationData) {
        throw new Error('Reservation data is missing');
      }
      
      let reservationId;
      
      // Create the actual reservation in Firestore
      try {
        if (userId) {
          // Use regular createReservation if user is logged in
          if (process.env.NODE_ENV === 'development') {
            console.log('Creating reservation with authenticated user');
          }
          
          // Clean the data to ensure no undefined values which Firestore won't accept
          const cleanedData = JSON.parse(JSON.stringify({
            ...reservationData,
            paymentStatus: status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending',
            payment: paymentInfo,
            paymentHistory: [paymentInfo]
          }));
          
          reservationId = await createReservation(cleanedData);
        } else {
          // For anonymous users, use the public reservation service
          if (process.env.NODE_ENV === 'development') {
            console.log('Creating reservation for anonymous user');
          }
          const { createManualReservation } = await import('../firebase/services/publicReservationService');
          
          if (!createManualReservation) {
            throw new Error('Public reservation service not available');
          }
          
          // Clean the data to ensure no undefined values which Firestore won't accept
          const cleanedData = JSON.parse(JSON.stringify({
            ...reservationData,
            paymentStatus: status === 'COMPLETED' || status === 'succeeded' ? 'paid' : 'pending',
            payment: paymentInfo,
            paymentHistory: [paymentInfo]
          }));
          
          // Create reservation for anonymous user
          reservationId = await createManualReservation(cleanedData);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Successfully created reservation with ID:', reservationId);
        }
      } catch (createError) {
        console.error('Error creating reservation in Firestore:', createError);
        // If we can't create it in Firestore but payment was successful,
        // still show success to the user but log the error
        // Ideally this would store the failed reservations somewhere for recovery
        reservationId = generateReservationId(); // Generate a temporary ID
      }
      
      // If a promo code was used, increment its usage count
      if (promoCodeApplied && promoCodeDetails?.id) {
        try {
          await applyPromoCode(promoCodeDetails.id);
          console.log(`Promo code ${promoCode} applied successfully`);
        } catch (err) {
          console.error('Error applying promo code:', err);
          // Don't stop reservation process if promo code update fails
        }
      }
      
      // Store the reservation ID from the payment process
      setReservationId(reservationId);
      setReservationSuccess(true);
      
      // Clear processing state
      setPaymentProcessing(false);
      
      // Clear the confirmed vessel from session storage
      sessionStorage.removeItem('confirmedAvailableVessel');
      setConfirmedVesselId(null);
      
      // Clean up localStorage
      localStorage.removeItem('reservationData');
    } catch (error) {
      console.error('Error completing reservation:', error);
      setError('Error finalizing reservation. Please contact support.');
      
      // Clear processing state on error
      setPaymentProcessing(false);
    }
  };
  
  // Handle reservation cancellation/error
  const handleReservationError = (error: string) => {
    setError(error);
    setPaymentProcessing(false); // Clear processing state on error
    setActiveStep(2); // Go back to customer info
  };
  
  // Step navigation
  const handleNext = () => {
    // Clear any existing errors when moving to next step
    setError(null);
    
    // Track the current step before proceeding
    if (process.env.NODE_ENV === 'development') {
      console.log('Handling next step:', {
        currentStep: activeStep,
        steps,
        hasSelectedType: !!selectedType,
        hasSelectedDate: !!selectedDate,
        hasTimeSlot: !!timeSlot,
        hasCustomerInfo: !!(firstName && lastName && email && phone),
        waiverAccepted,
        policyAgreed
      });
    }

    // Track step completion
    sessionRecorder.trackFunnelEvent({
      type: 'step_completed',
      data: {
        step: steps[activeStep].toLowerCase().replace(/\s+/g, '_')
      }
    });

    trackFunnelStep({
      step: steps[activeStep].toLowerCase().replace(/\s+/g, '_'),
      customerId: userId || 'guest',
      sessionId: sessionId,
      data: {
        vesselType: selectedType,
        startDate: selectedDate,
        endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
        timeSlot: timeSlot,
        totalPrice: totalPrice,
        originalPrice: originalPrice,
        discountAmount: discountAmount,
        promoCode: promoCode,
        lastStep: activeStep,
        errorMessage: error
      },
      metadata: {
        deviceInfo: navigator.userAgent,
        browserInfo: navigator.appName,
        lastActivity: new Date()
      }
    }).catch(console.error);

    if (activeStep === 0) {
      handleProceedToDateSelection();
    } else if (activeStep === 2) {
      // Validate customer information
      if (!firstName || !lastName || !email || !phone || !dob) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate age (must be at least 18 years old)
      const birthDate = new Date(dob);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        setError('You must be at least 18 years old to make a reservation');
        return;
      }
      
      // Validate ID information
      if (!usePassport && (!dlNumber || !dlState)) {
        setError("Please provide driver's license information");
        return;
      }
      
      if (usePassport && (!passportNumber || !passportCountry)) {
        setError("Please provide passport information");
        return;
      }
      
      // Validate people counts
      if (childrenCount === '' || operatorsCount === '' || parseInt(operatorsCount) < 1) {
        setError("Please provide valid passenger information");
        return;
      }
      
      setActiveStep(3); // Proceed to damage waiver
    } else if (activeStep === 3) {
      if (waiverAccepted === null) {
        setError('Please make a selection regarding the damage waiver');
        return;
      }
      setActiveStep(4); // Proceed to rental policies
    } else if (activeStep === 4) {
      // Check if the user has agreed to the policies
      if (!policyAgreed) {
        setError('Please acknowledge that you have read and agree to the rental policies to continue.');
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Moving to payment step');
      }
      
      // Create reservation data and store it in localStorage before moving to payment step
      try {
        if (!selectedDate || !timeSlot || !confirmedVesselId) {
          setError('Missing reservation details. Please start over.');
          return;
        }
        
        const dateString = format(selectedDate, 'yyyy-MM-dd');
        
        // Calculate the final price including damage waiver if accepted
        const waiverCost = waiverAccepted ? waiverAmount : 0;
        const subtotal = totalPrice + waiverCost;
        const finalTaxAmount = calculateTax(subtotal);
        const finalPrice = subtotal + finalTaxAmount;
        
        // Create the reservation data object
        const reservationData = {
          customerId: userId ? userId : 'guest-' + Date.now(),
          customerDetails: {
            firstName,
            lastName,
            email,
            phone,
            dateOfBirth: dob,
            identification: usePassport 
              ? { type: 'passport', number: passportNumber, country: passportCountry }
              : { type: 'driversLicense', number: dlNumber, state: dlState },
            childrenCount: parseInt(childrenCount),
            operatorsCount: parseInt(operatorsCount)
          },
          vesselId: confirmedVesselId,
          vesselName: '', // Will be filled in by the server
          vesselType: selectedType,
          partySize: parseInt(partySize),
          date: dateString,
          timeSlot,
          startTime,
          endTime,
          rentalPrice: totalPrice, // Base rental price before waiver
          damageWaiverAccepted: waiverAccepted,
          damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
          taxAmount: finalTaxAmount, // Tax on total including waiver if selected
          finalPrice: finalPrice, // Final price including tax
          originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
          discountAmount: discountAmount > 0 ? discountAmount : 0,
          discountId: promoCodeApplied && promoCodeDetails?.id ? promoCodeDetails.id : null,
          discountType: promoCodeApplied ? 'promo' : null,
          promoCode: promoCodeApplied ? promoCode : null,
          notes,
          status: 'confirmed',
          policyAgreed: policyAgreed,
          policyAgreedTimestamp: new Date().toISOString()
        };

        console.log('Storing reservation data with pricing:');
        console.log('Total Price (after discounts):', totalPrice);
        console.log('Original Price (before discount):', originalPrice);
        console.log('Discount Amount:', discountAmount);
        console.log('Final Price (with tax and waiver):', finalPrice);

        // Calculate and store deposit info if applicable
        if (settings?.reservationRules?.depositRequired) {
          const totalWithWaiverAndTax = totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0));
          const calculatedDeposit = settings.reservationRules.depositType === 'percentage' ? 
            (totalWithWaiverAndTax * settings.reservationRules.depositAmount / 100) : 
            settings.reservationRules.depositAmount;
          
          // Round the deposit amount to nearest quarter
          const roundedDeposit = roundToNearestQuarter(calculatedDeposit);
          
          // Store in localStorage for the payment form to access
          localStorage.setItem('reservationData', JSON.stringify({
            ...reservationData,
            deposit: roundedDeposit,
            isDepositPayment: true,  // Explicitly mark this as a deposit payment
            depositPercentage: settings.reservationRules.depositType === 'percentage' ? settings.reservationRules.depositAmount : null,
            depositType: settings.reservationRules.depositType,
            rentalPrice: totalPrice,  // totalPrice already includes any discount
            damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
            taxAmount: finalTaxAmount,
            finalPrice: totalWithWaiverAndTax,  // this will also include any discount since totalPrice does
            originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
            discountAmount: discountAmount > 0 ? discountAmount : 0,
            promoCode: promoCodeApplied ? promoCode : null
          }));
        } else {
          // If this is a 100% discount (price near 0), force the finalPrice to be exactly 0
          const finalPriceForStorage = (finalPrice === 0 || Math.abs(finalPrice) < 0.01) ? 0 : finalPrice;
          
          // Store without deposit info
          localStorage.setItem('reservationData', JSON.stringify({
            ...reservationData,
            rentalPrice: totalPrice,  // totalPrice already includes any discount
            damageWaiverAmount: waiverAccepted ? waiverAmount : 0,
            taxAmount: finalTaxAmount,
            finalPrice: finalPriceForStorage,  // Ensure exact 0 for zero-dollar reservations
            originalPrice: discountAmount > 0 ? originalPrice : totalPrice,
            discountAmount: discountAmount > 0 ? discountAmount : 0,
            promoCode: promoCodeApplied ? promoCode : null
          }));
          
          console.log('Updated localStorage with finalPrice:', finalPriceForStorage);
        }
        
        // Store for payment processing
        setReservationData(reservationData);
        
        // Trigger a storage event so MultiPaymentForm immediately refreshes
        window.dispatchEvent(new Event('storage'));
        
        // Move to payment step
        setActiveStep(5);
        
        console.log('Payment step state:', {
          step: 5,
          reservationData,
          settings,
          totalPrice,
          finalPrice,
          depositAmount: settings?.reservationRules?.depositType === 'percentage' ?
            ((totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0))) * settings.reservationRules.depositAmount / 100) :
            settings?.reservationRules?.depositAmount || 0,
          customerInfo: {
            firstName,
            lastName,
            email,
            phone
          }
        });
      } catch (error) {
        console.error('Error creating reservation data for payment step:', error);
        setError('Error preparing reservation data. Please try again.');
        return;
      }
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };
  
  const handleBack = () => {
    // Clear any existing errors when going back
    setError(null);
    
    if (activeStep === 0) {
      onClose();
    } else if (activeStep === 1) {
      // Smart navigation within the date/time selection step
      if (selectedDate && showTimeSlots) {
        // User is in time slot selector, go back to calendar
        setSelectedDate(null);
        setShowTimeSlots(false);
        setConfirmedVesselId(null);
      } else if (selectedDate && !showTimeSlots) {
        // User is in summary view, go back to time slot selector
        setShowTimeSlots(true);
        setConfirmedVesselId(null);
      } else {
        // User is in calendar view, go back to vessel selection
        setActiveStep((prevStep) => prevStep - 1);
      }
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };
  
  // Handle dialog close
  const handleClose = () => {
    if (activeStep > 0 && activeStep < 4) { // Only track if user has started the booking process
      // Track abandonment
      sessionRecorder.trackFunnelEvent({
        type: 'abandoned',
        data: {
          step: steps[activeStep].toLowerCase().replace(/\s+/g, '_')
        }
      });

      trackFunnelStep({
        step: 'abandoned',
        customerId: userId || 'guest',
        sessionId: sessionId,
        data: {
          vesselType: selectedType,
          startDate: selectedDate,
          endDate: selectedDate ? new Date(selectedDate.getTime() + (parseInt(duration) * 60 * 60 * 1000)) : null,
          timeSlot: timeSlot,
          totalPrice: totalPrice,
          originalPrice: originalPrice,
          discountAmount: discountAmount,
          promoCode: promoCode,
          lastStep: activeStep,
          errorMessage: error
        },
        metadata: {
          deviceInfo: navigator.userAgent,
          browserInfo: navigator.appName,
          lastActivity: new Date()
        }
      }).catch(console.error);
    }
    
    // If navigation is disabled, don't close the modal on subdomain
    if (disableNavigation) {
      // Keep the modal open
      return;
    }
    
    onClose();
    
    // Reset all state if user closes
    setActiveStep(0);
    setSelectedType('');
    setPartySize('');
    setSelectedDate(null);
    setTimeSlot('3');
    setReservationData(null);
    setConfirmedVesselId(null);
    setReservationSuccess(false);
    setReservationId(null);
    setWaiverAccepted(null);
    setPolicyAgreed(false);
    setPaymentProcessing(false); // Reset payment processing state
    
    // Reset customer information
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setNotes('');
    setDob('');
    setDlNumber('');
    setDlState('');
    setUsePassport(false);
    setPassportNumber('');
    setPassportCountry('');
    setChildrenCount('0');
    setOperatorsCount('1');
    
    // Reset promo code state
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoCodeDetails(null);
    setPromoCodeError(null);
    setDiscountAmount(0);
    setOriginalPrice(0);
  };

  // Render different content based on the active step
  // Helper function to safely render damage waiver policy content
  const renderDamageWaiverPolicy = (content: string) => {
    const isHtmlContent = (content: string) => {
      return /<[a-z][\s\S]*>/i.test(content);
    };

    // Helper function to replace variables with actual values
    const replaceVariables = (content: string) => {
      if (!content || !settings) return content;
      
      const variables: Record<string, string> = {
        businessName: settings.businessName || 'Marina',
        businessPhone: settings.phone || '',
        businessEmail: settings.email || ''
      };

      let processedContent = content;
      Object.entries(variables).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        processedContent = processedContent.replace(regex, value);
      });

      return processedContent;
    };
    
    const processedContent = replaceVariables(content);
    
    if (isHtmlContent(processedContent)) {
      // Render as HTML for formatted content
      return (
        <Box 
          component="div"
          sx={{ 
            mt: 1,
            '& p': { margin: '4px 0' },
            '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
            '& strong': { fontWeight: 600 },
            '& em': { fontStyle: 'italic' },
            '& a': { color: 'primary.main', textDecoration: 'underline' },
            '& .policy-variable': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              padding: '1px 4px',
              borderRadius: '3px',
              color: 'primary.main',
              fontWeight: 500
            },
            fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem'
          }}
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
      );
    } else {
      // Render as plain text for backwards compatibility
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1,
            fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem'
          }}
        >
          {processedContent}
        </Typography>
      );
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      // Step 1: Vessel selection
      case 0:
        return (
          <>
            {loading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
            ) : vesselTypes.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                No vessels are currently available for reservation.
              </Alert>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={2} justifyContent="center">
                      {vesselTypes.map((type) => (
                        <Grid item xs={6} sm={4} md={3} key={type}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: selectedType === type ? 2 : 1,
                              borderColor: selectedType === type ? 'primary.main' : 'grey.300',
                              backgroundColor: selectedType === type ? 'primary.light' : 'background.paper',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: selectedType === type ? 'primary.light' : 'grey.50',
                                transform: 'translateY(-2px)',
                                boxShadow: 2
                              },
                              height: '100%',
                              minHeight: 120
                            }}
                            onClick={() => {
                              setSelectedType(type);
                              setError(null); // Clear errors when selecting vessel type
                            }}
                          >
                            <CardContent sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              height: '100%',
                              p: 1.5,
                              '&:last-child': { pb: 1.5 }
                            }}>
                              <Box sx={{ 
                                color: selectedType === type ? 'primary.main' : 'text.secondary',
                                fontSize: '2rem',
                                mb: 1
                              }}>
                                {getVesselTypeIcon(type)}
                              </Box>
                              <Typography 
                                variant="body2" 
                                textAlign="center"
                                fontWeight={selectedType === type ? 'bold' : 'normal'}
                                color={selectedType === type ? 'primary.main' : 'text.primary'}
                                sx={{ lineHeight: 1.2 }}
                              >
                                {formatVesselTypeName(type)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="party-size-label">Number of People in Party</InputLabel>
                      <Select
                        labelId="party-size-label"
                        value={partySize}
                        label="Number of People in Party"
                        onChange={(e) => {
                          setPartySize(e.target.value as string);
                          setError(null); // Clear errors when changing party size
                        }}
                        size="small"
                      >
                        {Array.from({ length: getMaxCapacity() }, (_, i) => i + 1).map((num) => (
                          <MenuItem key={num} value={num.toString()}>
                            {num}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                        Maximum capacity: {getMaxCapacity()} people
                      </Typography>
                    </FormControl>
                  </Grid>
                  
                  {/* Only show duration selection after vessel type and party size are selected */}
                  {selectedType && partySize && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Rental Duration
                      </Typography>                      
                      {loadingDurations ? (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ ml: 1 }}>Loading pricing options...</Typography>
                        </Box>
                      ) : (
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {durationOptions.map((option) => (
                            <Paper
                              key={option.hours}
                              elevation={selectedDuration === option.hours ? 3 : 1}
                              sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: selectedDuration === option.hours ? 2 : 0,
                                borderColor: 'primary.main',
                                backgroundColor: selectedDuration === option.hours ? 'primary.light' : 'background.paper',
                                flex: 1,
                                minWidth: 200
                              }}
                              onClick={() => setSelectedDuration(option.hours)}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Typography variant="h6" fontWeight="bold" color={selectedDuration === option.hours ? 'primary.main' : 'text.primary'}>
                                  {option.hours} Hour{option.hours !== 1 ? 's' : ''}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                  ${option.price}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                {option.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ${(option.price / option.hours).toFixed(0)} per hour
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      )}
                      
                      {durationOptions.length === 0 && !loadingDurations && selectedType && (
                        <Alert severity="warning">
                          No duration options available for {selectedType}. Please try a different vessel type.
                        </Alert>
                      )}
                    </Grid>
                  )}
                  
                  {/* Show information message when vessel type or party size is not selected */}
                  {(!selectedType || !partySize) && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Please select a vessel type and party size to see available rental duration options.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </>
        );
        
      // Step 2: Date and time selection
      case 1:
        return (
          <Box>
            {/* Show calendar first for date selection */}
            {!selectedDate && (
              <AvailabilityCalendar
                vesselType={selectedType}
                onDateSelect={handleDateSelect}
                onTimeSlotSelect={handleTimeSlotSelect}
              />
            )}
            
            {/* Show message when date is selected but duration is not */}
            {selectedDate && showTimeSlots && !selectedDuration && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please go back and select a rental duration to see available time slots.
              </Alert>
            )}

            {/* Show multi-day time slot selector when date and duration are selected */}
            {selectedDate && showTimeSlots && selectedDuration && (
              <MultiDayTimeSlotSelector
                selectedDate={selectedDate}
                vesselType={selectedType}
                availableVessels={availableVessels}
                selectedDuration={selectedDuration}
                onTimeSlotSelect={handleMultiDayTimeSlotSelect}
                onDateChange={handleDateChangeFromMultiDay}
                onDurationChange={handleDurationChange}
                durationOptions={durationOptions}
              />
            )}
            
            {/* Selected date and time summary */}
            {selectedDate && !showTimeSlots && (
              <Box 
                sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}
                id="reservation-summary"
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Date:</strong> {format(selectedDate, 'MMMM d, yyyy')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Time:</strong> {startTime} - {endTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Duration:</strong> {duration} {parseInt(duration) === 1 ? 'hour' : 'hours'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" fontWeight="bold">
                      <strong>Rental Duration Price:</strong> ${totalPrice.toFixed(2)}
                      {settings?.reservationRules?.salesTaxRate > 0 && ' +Tax'}
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Back to date/time selection button */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setShowTimeSlots(true);
                      setConfirmedVesselId(null);
                    }}
                  >
                    Change Date/Time
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
        
      // Step 3: Customer Information
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setError(null); // Clear errors when changing first name
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setError(null); // Clear errors when changing last name
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null); // Clear errors when changing email
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Phone Number"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(xxx) xxx-xxxx"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={usePassport}
                        onChange={(e) => setUsePassport(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Use Passport Instead of Driver's License"
                  />
                </Grid>
                
                {!usePassport ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Driver's License Number"
                        value={dlNumber}
                        onChange={(e) => setDlNumber(e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl required fullWidth size="small">
                        <InputLabel id="dl-state-label">Driver's License State</InputLabel>
                        <Select<string>
                          labelId="dl-state-label"
                          value={dlState}
                          label="Driver's License State"
                          onChange={handleDlStateChange}
                        >
                          {US_STATES.map(state => (
                            <MenuItem key={state.code} value={state.code}>
                              {state.code} - {state.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Passport Number"
                        value={passportNumber}
                        onChange={(e) => setPassportNumber(e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl required fullWidth size="small">
                        <InputLabel id="passport-country-label">Passport Country</InputLabel>
                        <Select<string>
                          labelId="passport-country-label"
                          value={passportCountry}
                          label="Passport Country"
                          onChange={handlePassportCountryChange}
                        >
                          {COUNTRIES.map(country => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Number of People 12 Years or Under"
                    type="number"
                    value={childrenCount}
                    onChange={(e) => setChildrenCount(e.target.value)}
                    InputProps={{ inputProps: { min: 0 } }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Number of People Planning to Operate"
                    type="number"
                    value={operatorsCount}
                    onChange={(e) => setOperatorsCount(e.target.value)}
                    InputProps={{ inputProps: { min: 1 } }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );
        
      // Step 4: Damage Waiver
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Damage Protection
            </Typography>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              
              {/* Show custom damage waiver policy if available */}
              {settings?.reservationRules?.damageWaiverPolicy ? (
                <Box sx={{ mt: 1 }}>
                  {renderDamageWaiverPolicy(settings.reservationRules.damageWaiverPolicy)}
                </Box>
              ) : (
                <>
                  {/* Fallback to default content */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 1,
                      fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem' 
                    }}
                  >
                    Our vessels require significant repair costs if damaged:
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mt: 0.5, mb: 1 }}>
                    <Grid item xs={4}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem' }}
                      >
                        • General repairs: $1,000-$5,000
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem' }}
                      >
                        • Motor replacement: $10,000+
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.875rem' }}
                      >
                        • Vessel replacement: $50,000+
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Use legacy custom text if available */}
                  {damageWaiverSettings?.uiSettings?.customText && (
                    <Box sx={{ mt: 1 }}>
                      {renderDamageWaiverPolicy(damageWaiverSettings.uiSettings.customText)}
                    </Box>
                  )}
                </>
              )}
            </Paper>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper 
                  sx={{ 
                    p: 1.5, 
                    height: '100%',
                    cursor: 'pointer',
                    border: waiverAccepted === true ? 
                      `2px solid ${damageWaiverSettings?.uiSettings?.accentColor || '#4caf50'}` : 
                      '1px solid #e0e0e0',
                    bgcolor: waiverAccepted === true ? 
                      `rgba(${damageWaiverSettings?.uiSettings?.accentColor ? hexToRgb(damageWaiverSettings.uiSettings.accentColor) : '76, 175, 80'}, 0.1)` : 
                      'transparent'
                  }}
                  onClick={() => {
                    setWaiverAccepted(true);
                    setError(null); // Clear errors when accepting waiver
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: damageWaiverSettings?.uiSettings?.primaryColor || '#1976d2',
                      fontSize: damageWaiverSettings?.uiSettings?.headingFontSize || '1rem'
                    }}
                  >
                    YES, I accept the damage waiver (${waiverAmount.toFixed(2)})
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.8rem' 
                    }}
                  >
                    My financial liability for accidental damage will be limited.
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={6}>
                <Paper 
                  sx={{ 
                    p: 1.5,
                    height: '100%',
                    cursor: 'pointer',
                    border: waiverAccepted === false ? '2px solid #f44336' : '1px solid #e0e0e0',
                    bgcolor: waiverAccepted === false ? 'rgba(244, 67, 54, 0.1)' : 'transparent'
                  }}
                  onClick={() => {
                    setWaiverAccepted(false);
                    setError(null); // Clear errors when declining waiver
                  }}
                >
                  <Typography variant="subtitle2" color="error">
                    NO, I decline the damage waiver
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: damageWaiverSettings?.uiSettings?.textFontSize || '0.8rem' 
                    }}
                  >
                    I accept full financial responsibility for ANY damage up to full replacement cost.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        );
        
      // Step 5: Rental Policies
      case 4:
        const formatCancellationDeadline = (hours: number) => {
          if (hours === 0) return 'No advance notice required';
          if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} before reservation time`;
          const days = Math.floor(hours / 24);
          const remainingHours = hours % 24;
          if (remainingHours === 0) return `${days} day${days !== 1 ? 's' : ''} before reservation time`;
          return `${days} day${days !== 1 ? 's' : ''} and ${remainingHours} hour${remainingHours !== 1 ? 's' : ''} before reservation time`;
        };

        // Helper function to determine if content is HTML or plain text
        const isHtmlContent = (content: string) => {
          return /<[a-z][\s\S]*>/i.test(content);
        };

        // Helper function to replace variables with actual values
        const replaceVariables = (content: string) => {
          if (!content || !settings) return content;
          
          const formatCancellationDeadlineText = (hours: number) => {
            if (hours === 0) return 'no advance notice required';
            if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''}`;
            const days = Math.floor(hours / 24);
            const remainingHours = hours % 24;
            if (remainingHours === 0) return `${days} day${days !== 1 ? 's' : ''}`;
            return `${days} day${days !== 1 ? 's' : ''} and ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
          };

          const formatDepositAmount = () => {
            if (!settings.reservationRules?.depositRequired) return 'No deposit required';
            const { depositType, depositAmount } = settings.reservationRules;
            return depositType === 'fixed' ? `$${depositAmount}` : `${depositAmount}%`;
          };

          const variables: Record<string, string> = {
            cancellationDeadline: formatCancellationDeadlineText(settings.reservationRules?.cancellationDeadlineHours || 24),
            depositAmount: formatDepositAmount(),
            depositType: settings.reservationRules?.depositType === 'fixed' ? 'fixed amount' : 'percentage of total',
            baseRate: `$${settings.reservationRules?.baseRate || 0}`,
            salesTaxRate: `${settings.reservationRules?.salesTaxRate || 0}%`,
            businessName: settings.businessName || 'Marina',
            businessPhone: settings.phone || '',
            businessEmail: settings.email || ''
          };

          let processedContent = content;
          Object.entries(variables).forEach(([key, value]) => {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            processedContent = processedContent.replace(regex, value);
          });

          return processedContent;
        };

        // Helper function to safely render cancellation policy content
        const renderCancellationPolicy = (content: string) => {
          const processedContent = replaceVariables(content);
          
          if (isHtmlContent(processedContent)) {
            // Render as HTML for formatted content
            return (
              <Box 
                component="div"
                sx={{ 
                  pl: 2,
                  '& p': { margin: '4px 0' },
                  '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                  '& .policy-variable': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    color: 'primary.main',
                    fontWeight: 500
                  }
                }}
                dangerouslySetInnerHTML={{ __html: processedContent }}
              />
            );
          } else {
            // Render as plain text for backwards compatibility
            return (
              <Typography variant="body2" sx={{ pl: 2 }}>
                {processedContent}
              </Typography>
            );
          }
        };

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Rental Policies
            </Typography>
            
            {/* Display Policies */}
            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
 
              
              {/* Cancellation Deadline */}
              {settings?.reservationRules?.cancellationDeadlineHours !== undefined && (
                <Typography variant="body2" paragraph>
                  <strong>Cancellation Deadline:</strong> Reservations can be cancelled up to{' '}
                  {formatCancellationDeadline(settings.reservationRules.cancellationDeadlineHours)}
                </Typography>
              )}
              
              {/* Cancellation Policy Text */}
              {(settings?.reservationRules?.cancellationPolicy || policies?.cancellationPolicy) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <strong>Cancellation Policy:</strong>
                  </Typography>
                  {renderCancellationPolicy(settings?.reservationRules?.cancellationPolicy || policies?.cancellationPolicy || '')}
                </Box>
              )}
              
              {/* Refund Policy Text */}
              {(settings?.reservationRules?.refundPolicy || policies?.refundPolicy) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    <strong>Refund Policy:</strong>
                  </Typography>
                  {renderCancellationPolicy(settings?.reservationRules?.refundPolicy || policies?.refundPolicy || '')}
                </Box>
              )}
              
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={policyAgreed}
                      onChange={(e) => {
                        setPolicyAgreed(e.target.checked);
                        setError(null); // Clear errors when agreeing to policies
                      }}
                      color="primary"
                      required
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I acknowledge that I have read and agree to the rental policies stated above, including the cancellation deadline and policy.
                    </Typography>
                  }
                />
              </Box>
            </Paper>
          </Box>
        );
        
      // Step 6: Payment
      case 5:
        console.log('Rendering payment step:', {
          reservationSuccess,
          reservationId,
          hasSettings: !!settings,
          totalPrice,
          waiverAccepted,
          waiverAmount,
          customerInfo: {
            firstName,
            lastName,
            email,
            phone
          }
        });
        return (
          <Box>
            {reservationSuccess ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Reservation Complete!
                </Typography>
                <Typography paragraph>
                  Your reservation has been confirmed. Reservation ID: <strong>{typeof reservationId === 'object' ? 'Confirmed' : reservationId}</strong>
                </Typography>
                <Typography>
                  You will receive a confirmation email shortly with all the details of your reservation.
                </Typography>
              </Alert>
            ) : (
              <>
                {isStaffMode && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Staff Mode: Payment Options
                    </Typography>
                    <Typography variant="body2">
                      You can either process payment now or skip payment to create an unpaid reservation.
                    </Typography>
                  </Alert>
                )}
                
                <Box sx={{ mt: 3 }}>
                  <MultiPaymentForm
                    amount={settings?.reservationRules?.depositRequired ? 
                      settings.reservationRules.depositType === 'fixed' ?
                        settings.reservationRules.depositAmount :
                        roundToNearestQuarter(
                          (totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0))) * 
                          (settings.reservationRules.depositAmount / 100)
                        )
                      :
                      (totalPrice + (waiverAccepted ? waiverAmount : 0) + calculateTax(totalPrice + (waiverAccepted ? waiverAmount : 0)))
                    }
                    paymentType={settings?.reservationRules?.depositRequired ? "Deposit Amount" : "Full Payment"}
                    customerInfo={{
                      firstName,
                      lastName,
                      email,
                      phone
                    }}
                    onPaymentComplete={handleReservationComplete}
                    onPaymentError={handleReservationError}
                    disabled={paymentProcessing}
                  />
                </Box>
                
                {isStaffMode && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="large"
                      onClick={() => handleReservationComplete('STAFF_SKIP_PAYMENT', 'unpaid', 'staff')}
                      disabled={paymentProcessing}
                    >
                      Skip Payment (Create Unpaid Reservation)
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
        
      default:
        return null;
    }
  };

  // Helper function to convert hex color to RGB values
  const hexToRgb = (hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Convert 3-digit hex to 6-digit
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Return as RGB string
    return `${r}, ${g}, ${b}`;
  };

  // Function to get appropriate icon for vessel type
  const getVesselTypeIcon = (type: string) => {
    const normalizedType = type.toLowerCase();
    
    if (normalizedType.includes('pontoon')) return <BoatIcon />;
    if (normalizedType.includes('fishing')) return <FishingIcon />;
    if (normalizedType.includes('speedboat') || normalizedType.includes('speed')) return <SpeedboatIcon />;
    if (normalizedType.includes('jet ski')) return <JetSkiIcon />;
    if (normalizedType.includes('cabin') || normalizedType.includes('cruiser') || normalizedType.includes('sailing')) return <SailingIcon />;
    if (normalizedType.includes('ski boat') || normalizedType.includes('center console')) return <BoatIcon />;
    if (normalizedType.includes('kayak')) return <KayakIcon />;
    if (normalizedType.includes('canoe')) return <KayakIcon />;
    if (normalizedType.includes('paddleboard') || normalizedType.includes('sup')) return <PaddleboardIcon />;
    if (normalizedType.includes('rowboat')) return <RowboatIcon />;
    if (normalizedType.includes('pedal boat')) return <RowboatIcon />;
    if (normalizedType.includes('raft')) return <KayakIcon />;
    
    // Default icon
    return <BoatIcon />;
  };

  // Function to format vessel type display name
  const formatVesselTypeName = (type: string) => {
    return type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="reservation-modal-title"
    >
      <DialogTitle id="reservation-modal-title">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%' }}>
          <Typography variant="h5" component="span" sx={{ fontWeight: 600, textAlign: 'left', width: '100%' }}>
            {activeStep === 5 && reservationSuccess 
              ? "Reservation Confirmed" 
              : isStaffMode 
                ? "Staff Reservation" 
                : "Book Your Boat"}
          </Typography>
          {!reservationSuccess && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mt: 1,
                bgcolor: 'grey.100',
                borderRadius: 2,
                px: 2,
                py: 1,
                minWidth: 120,
                boxShadow: 1
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: 18,
                  mr: 1.5
                }}
              >
                {activeStep + 1}
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {steps[activeStep]}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 2 }}>
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Loading indicator */}
        {loading && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Step content */}
        {!loading && renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ p: 1.5 }}>
        {!(activeStep === 5 && reservationSuccess) && (
          <>
            <Button onClick={handleBack}>
              {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            {activeStep < 5 && (
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && (!selectedType || !partySize || !selectedDuration || parseInt(partySize) > getMaxCapacity())) ||
                  (activeStep === 1 && (!selectedDate || showTimeSlots)) ||
                  (activeStep === 2 && (!firstName || !lastName || !email || !phone || !dob))
                }
              >
                {activeStep === 4 ? 'Proceed to Payment' : 'Continue'}
              </Button>
            )}
          </>
        )}
        
        {activeStep === 5 && reservationSuccess && (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReservationModal; 